import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import type { TaskWithRelations, User, UserWithTeam } from '@shared/schema';
import { useDrop } from 'react-dnd';
import TaskCard from './task-card';
import { useUnreadComments } from '@/contexts/UnreadCommentsContext';

interface StaffColumnProps {
  user: UserWithTeam;
  tasks: TaskWithRelations[];
  onTaskDrop: (taskId: string, targetUserId: string) => void;
  currentUser?: User;
  onTaskStatusUpdate?: (taskId: string, status: string) => void;
  onTaskEdit?: (task: TaskWithRelations) => void;
  onTaskView?: (task: TaskWithRelations) => void;
  onAddTask?: (userId: string) => void;
  onNeedHelp?: (task: TaskWithRelations) => void;
  onAssignToMe?: (taskId: string) => void;
  onPriorityUpdate?: (taskId: string, priority: number) => void;
  isUnassigned?: boolean;
  readOnly?: boolean;
  globalCollapsed?: boolean;
  showCompletedTasks?: boolean;
}

export default function StaffColumn({
  user,
  tasks,
  onTaskDrop,
  currentUser,
  onTaskStatusUpdate,
  onTaskEdit,
  onTaskView,
  onAddTask,
  onNeedHelp,
  onAssignToMe,
  onPriorityUpdate,
  isUnassigned = false,
  readOnly = false,
  globalCollapsed = false,
  showCompletedTasks = false,
}: StaffColumnProps) {
  const { hasUnreadComments } = useUnreadComments();
  const [{ isOver }, drop] = useDrop(() => ({
    accept: 'task',
    canDrop: () => !readOnly,
    drop: (
      item: { id: string; assignedTo: string; priority: number },
      monitor
    ) => {
      if (readOnly) return;
      const targetUserId = isUnassigned ? '' : user.id;

      // If dropping on a different user, handle assignment
      if (item.assignedTo !== targetUserId) {
        onTaskDrop(item.id, targetUserId);
        return;
      }

      // If dropping on the same user, handle priority reordering
      if (item.assignedTo === targetUserId && onPriorityUpdate) {
        // Get the client offset to determine drop position
        const clientOffset = monitor.getClientOffset();
        if (!clientOffset) return;

        // Get the drop target element
        const dropTarget = monitor.getDropResult();
        if (!dropTarget) return;

        // Get all task elements in this column
        const taskElements = document.querySelectorAll(
          `[data-testid="staff-column-${user.id}"] [data-task-id]`
        );

        // Calculate new priority based on drop position
        let newPriority = 0;
        for (let i = 0; i < taskElements.length; i++) {
          const taskElement = taskElements[i] as HTMLElement;
          const taskRect = taskElement.getBoundingClientRect();

          if (clientOffset.y < taskRect.top + taskRect.height / 2) {
            // Insert before this task
            newPriority = i * 10;
            break;
          }
          newPriority = (i + 1) * 10;
        }

        // Only update if the priority actually changed
        if (newPriority !== (item.priority || 0)) {
          onPriorityUpdate(item.id, newPriority);
        }
      }
    },
    collect: monitor => ({
      isOver: monitor.isOver(),
    }),
  }));

  const getInitials = (firstName?: string, lastName?: string) => {
    if (!firstName && !lastName) return 'U';
    return `${firstName?.[0] || ''}${lastName?.[0] || ''}`.toUpperCase();
  };

  // Calculate workload (this is a simple calculation - in real app you might want more sophisticated logic)
  const totalTasks = (() => {
    // Always exclude dropped tasks from count
    const tasksWithoutDropped = tasks.filter(task => task.status !== 'dropped');
    // If showCompletedTasks is OFF, count only active tasks
    if (!showCompletedTasks) {
      return tasksWithoutDropped.filter(
        task => !['complete', 'pending_approval'].includes(task.status)
      ).length;
    }
    // If showCompletedTasks is ON, count all tasks except dropped
    return tasksWithoutDropped.length;
  })();
  const activeTasks = tasks.filter(t =>
    ['started', 'working'].includes(t.status)
  ).length;
  const workloadPercent = Math.min(100, (activeTasks / Math.max(1, 10)) * 100); // Assuming max 10 active tasks = 100%

  const getWorkloadColor = () => {
    if (workloadPercent >= 90) return 'text-red-600 dark:text-red-400';
    if (workloadPercent >= 70) return 'text-orange-600 dark:text-orange-400';
    if (workloadPercent >= 50) return 'text-yellow-600 dark:text-yellow-400';
    return 'text-green-600 dark:text-green-400';
  };

  const canAddTask =
    !readOnly &&
    (currentUser?.role === 'manager' || currentUser?.role === 'admin');

  return (
    <div className="w-72 sm:w-80 lg:w-96 flex-shrink-0 staff-column border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 lg:min-h-full min-h-96 shadow-sm">
      {/* Staff Header */}
      <div className="p-2 sm:p-4 bg-surface border-b border-gray-200 dark:border-gray-700 sticky top-0 z-10">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-2 sm:space-y-0">
          <div className="flex items-center space-x-2 sm:space-x-3 min-w-0">
            <Avatar className="w-8 h-8 sm:w-10 sm:h-10 flex-shrink-0">
              <AvatarImage src={user.profileImageUrl ?? undefined} />
              <AvatarFallback className="bg-primary text-primary-foreground font-medium text-xs sm:text-sm">
                {getInitials(user.firstName, user.lastName)}
              </AvatarFallback>
            </Avatar>
            <div className="min-w-0 flex-1">
              <h3 className="font-semibold text-gray-900 dark:text-gray-100 text-sm sm:text-base truncate">
                {user.firstName} {user.lastName}
              </h3>
              {/* <p className="text-xs sm:text-sm text-gray-500 dark:text-gray-400 truncate">
                {user.team?.name || 'No team assigned'}
              </p> */}
            </div>
          </div>
          <div className="text-right flex-shrink-0">
            <div className="text-xs sm:text-sm font-medium text-gray-900 dark:text-gray-100">
              {totalTasks} task{totalTasks !== 1 ? 's' : ''}
            </div>
            <div className={`text-xs ${getWorkloadColor()}`}>
              {Math.round(workloadPercent)}% capacity
            </div>
          </div>
        </div>
      </div>

      {/* Task List */}
      <div
        ref={drop}
        className={`p-2 sm:p-3 pb-6 h-full overflow-y-auto transition-all duration-200 ${
          isOver ? 'drop-zone-active' : ''
        }`}
        data-testid={`staff-column-${user.id}`}
      >
        {/* All Tasks Section - Show all tasks when toggle is ON, only active when OFF */}
        {tasks.some(task => !(task as any).isHelpTask) && (
          <>
            <div className="border-t border-gray-200 dark:border-gray-600 my-4 pt-4">
              <div className="flex items-center space-x-2 mb-3">
                <Badge
                  variant="outline"
                  className="bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-300 border-green-200 dark:border-green-700"
                >
                  📋 {showCompletedTasks ? 'All Tasks' : 'Active Tasks'}
                </Badge>
              </div>
            </div>
            {tasks
              .filter(task => {
                if ((task as any).isHelpTask) return false;
                // Always exclude dropped tasks from board view
                if (task.status === 'dropped') return false;
                // If showCompletedTasks is OFF, hide completed tasks
                if (!showCompletedTasks) {
                  return !['complete', 'pending_approval'].includes(
                    task.status
                  );
                }
                // If showCompletedTasks is ON, show all tasks except dropped
                return true;
              })
              .sort((a, b) => (a.priority || 0) - (b.priority || 0))
              .map((task, index) => (
                <div key={task.id} data-task-id={task.id}>
                  <TaskCard
                    task={task}
                    currentUser={currentUser}
                    isHelpTask={(task as any).isHelpTask}
                    canTrackTime={
                      currentUser?.id === task.assignedTo ||
                      currentUser?.role === 'manager' ||
                      currentUser?.role === 'admin'
                    }
                    onStatusUpdate={onTaskStatusUpdate}
                    onEdit={onTaskEdit}
                    taskNumber={index + 1}
                    onView={onTaskView}
                    onNeedHelp={onNeedHelp}
                    onAssignToMe={onAssignToMe}
                    onPriorityUpdate={onPriorityUpdate}
                    globalCollapsed={globalCollapsed}
                    hasUnreadComments={hasUnreadComments(task.id)}
                  />
                </div>
              ))}
          </>
        )}

        {/* Pending Approval Tasks Section */}
        {tasks.some(
          task =>
            !(task as any).isHelpTask && task.status === 'pending_approval'
        ) && (
          <>
            <div className="border-t border-gray-200 dark:border-gray-600 my-4 pt-4">
              <div className="flex items-center space-x-2 mb-3">
                <Badge
                  variant="outline"
                  className="bg-yellow-50 dark:bg-yellow-900/20 text-yellow-700 dark:text-yellow-300 border-yellow-200 dark:border-yellow-700"
                >
                  ⏳ Pending Approval
                </Badge>
              </div>
            </div>
            {tasks
              .filter(
                task =>
                  !(task as any).isHelpTask &&
                  task.status === 'pending_approval'
              )
              .sort((a, b) => (a.priority || 0) - (b.priority || 0))
              .map((task, index) => (
                <div
                  key={`pending-approval-${task.id}`}
                  className="relative mb-3"
                  data-task-id={task.id}
                >
                  <div className="absolute -left-2 top-2 w-1 h-8 bg-yellow-500 rounded-full"></div>
                  <TaskCard
                    task={task}
                    currentUser={currentUser}
                    isHelpTask={(task as any).isHelpTask}
                    canTrackTime={
                      currentUser?.id === task.assignedTo ||
                      currentUser?.role === 'manager' ||
                      currentUser?.role === 'admin'
                    }
                    onStatusUpdate={onTaskStatusUpdate}
                    onEdit={onTaskEdit}
                    taskNumber={index + 1}
                    onView={onTaskView}
                    onNeedHelp={onNeedHelp}
                    onAssignToMe={onAssignToMe}
                    onPriorityUpdate={onPriorityUpdate}
                    globalCollapsed={globalCollapsed}
                    hasUnreadComments={hasUnreadComments(task.id)}
                  />
                </div>
              ))}
          </>
        )}

        {/* Help Tasks Section */}
        {tasks.some(task => (task as any).isHelpTask) && (
          <>
            <div className="border-t border-gray-200 dark:border-gray-600 my-4 pt-4">
              <div className="flex items-center space-x-2 mb-3">
                <Badge
                  variant="outline"
                  className="bg-purple-50 dark:bg-purple-900/20 text-purple-700 dark:text-purple-300 border-purple-200 dark:border-purple-700"
                >
                  🆘 Help Requests
                </Badge>
              </div>
            </div>
            {tasks
              .filter(task => (task as any).isHelpTask)
              .map((task, index) => (
                <div
                  key={`help-${task.id}`}
                  className="relative mb-3"
                  data-task-id={task.id}
                >
                  <div className="absolute -left-2 top-2 w-1 h-8 bg-purple-500 rounded-full"></div>
                  <TaskCard
                    task={task}
                    currentUser={currentUser}
                    isHelpTask={(task as any).isHelpTask}
                    canTrackTime={
                      currentUser?.id === task.assignedTo ||
                      currentUser?.role === 'manager' ||
                      currentUser?.role === 'admin'
                    }
                    onStatusUpdate={onTaskStatusUpdate}
                    onEdit={onTaskEdit}
                    taskNumber={index + 1}
                    onView={onTaskView}
                    onNeedHelp={onNeedHelp}
                    onAssignToMe={onAssignToMe}
                    onPriorityUpdate={onPriorityUpdate}
                    globalCollapsed={globalCollapsed}
                    hasUnreadComments={hasUnreadComments(task.id)}
                  />
                </div>
              ))}
          </>
        )}

        {/* Add Task Button */}
        {canAddTask && (
          <Button
            variant="outline"
            className="w-full p-4 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg text-gray-500 dark:text-gray-400 hover:border-gray-400 dark:hover:border-gray-500 hover:text-gray-600 dark:hover:text-gray-300 transition-colors bg-transparent"
            // onClick={() => onAddTask?.(user.id)}
            data-testid={`button-add-task-${user.id}`}
          >
            {/* <Plus className="w-4 h-4 mr-2" /> */}
            {/* Add Task */}
          </Button>
        )}
      </div>
    </div>
  );
}
